import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Home } from 'lucide-react';
import { toast } from 'sonner';
import PlayerFollowButton from '../components/PlayerFollowButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const Followers = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [followingMap, setFollowingMap] = useState({});
  const [blockedMap, setBlockedMap] = useState({});
  const [busy, setBusy] = useState({});
  const [blocking, setBlocking] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Block confirmation dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);

  const load = async () => {
    try {
      const followersRes = await apiClient.get('/follow/followers');
      const followers = followersRes.data.followers || [];
      setRows(followers);
      
      // Following map - normalize IDs to strings
      const followMap = {};
      followers.forEach(u => {
        followMap[String(u.id)] = u.is_following_back || false;
      });
      setFollowingMap(followMap);
      
      // Block map - load all at once, normalize IDs to strings
      const blockPromises = followers.map(u =>
        apiClient.get(`/block/status/${u.id}`)
          .then(res => ({ id: String(u.id), blocked: res.data.i_blocked_them || false }))
          .catch(() => ({ id: String(u.id), blocked: false }))
      );
      
      const blockResults = await Promise.all(blockPromises);
      const blockMap = {};
      blockResults.forEach(r => {
        blockMap[r.id] = r.blocked;
      });
      
      setBlockedMap(blockMap);
    } catch (e) {
      console.error('Load failed', e);
      toast.error('Failed to load followers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const followBack = async (id, username) => {
    const userId = String(id);
    setBusy(p => ({ ...p, [userId]: true }));
    try {
      await apiClient.post(`/follow/${userId}`);
      setFollowingMap(prev => ({ ...prev, [userId]: true }));
      toast.success(`Following ${username}`);
    } catch (e) {
      console.error('Follow failed', e?.response?.data || e);
      toast.error('Failed to follow user');
    } finally {
      setBusy(p => ({ ...p, [userId]: false }));
    }
  };

  const unfollow = async (id, username) => {
    const userId = String(id);
    setBusy(p => ({ ...p, [userId]: true }));
    try {
      await apiClient.delete(`/follow/${userId}`);
      setFollowingMap(prev => ({ ...prev, [userId]: false }));
      toast.success(`Unfollowed ${username}`);
    } catch (e) {
      console.error('Unfollow failed', e?.response?.data || e);
      toast.error('Failed to unfollow user');
    } finally {
      setBusy(p => ({ ...p, [userId]: false }));
    }
  };

  // Open block confirmation dialog
  const handleBlockClick = (user) => {
    const userId = String(user.id);
    const isBlocked = !!blockedMap[userId];
    
    if (isBlocked) {
      // Unblock directly without confirmation
      executeUnblock(userId, user.username);
    } else {
      // Show confirmation dialog for blocking
      setUserToBlock(user);
      setBlockDialogOpen(true);
    }
  };

  // Execute block after confirmation
  const executeBlock = async () => {
    if (!userToBlock) return;
    
    const userId = String(userToBlock.id);
    const username = userToBlock.username;
    
    setBlockDialogOpen(false);
    setBlocking(prev => ({ ...prev, [userId]: true }));
    
    try {
      await apiClient.post(`/block/${userId}`);
      setBlockedMap(prev => ({ ...prev, [userId]: true }));
      setFollowingMap(prev => ({ ...prev, [userId]: false }));
      toast.success(`Blocked ${username}`);
    } catch (e) {
      console.error("Block failed", e?.response?.data || e);
      toast.error(`Failed to block ${username}`);
    } finally {
      setBlocking(prev => ({ ...prev, [userId]: false }));
      setUserToBlock(null);
    }
  };

  // Execute unblock
  const executeUnblock = async (userId, username) => {
    setBlocking(prev => ({ ...prev, [userId]: true }));
    
    try {
      await apiClient.delete(`/block/${userId}`);
      setBlockedMap(prev => ({ ...prev, [userId]: false }));
      toast.success(`Unblocked ${username}`);
    } catch (e) {
      console.error("Unblock failed", e?.response?.data || e);
      toast.error(`Failed to unblock ${username}`);
    } finally {
      setBlocking(prev => ({ ...prev, [userId]: false }));
    }
  };

  const getTierColor = (tier) => {
    const t = tier?.toLowerCase();
    if (t === 'elite') return 'text-purple-600 border-purple-300 bg-purple-50';
    if (t === 'baller') return 'text-blue-600 border-blue-300 bg-blue-50';
    if (t === 'role player') return 'text-green-600 border-green-300 bg-green-50';
    return 'text-gray-600 border-gray-300 bg-gray-50';
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="h-10 px-3 text-[14px] font-medium text-[#52525B] hover:text-[#111111] hover:bg-[#FFFFFF] rounded-[12px] gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="h-8 w-8 rounded-lg"
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="w-4 h-4 text-zinc-400" />
          </Button>
        </div>

        <section className="bg-[#FFFFFF] rounded-[16px] border border-[#E4E4E7] p-6 sm:p-8">
          <h1 className="text-[24px] sm:text-[32px] font-bold text-[#111111] tracking-[-0.02em] mb-6">Followers</h1>

          {loading ? (
            <div className="text-center py-8 text-[#71717A]">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#52525B] mb-4">No followers yet.</p>
              <p className="text-[14px] text-[#71717A]">Share your profile and start playing games to gain followers!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map(u => {
                const userId = String(u.id);
                const isBlocked = !!blockedMap[userId];
                
                return (
                  <div
                    key={u.id}
                    className="flex items-center justify-between p-4 border border-[#E4E4E7] rounded-[12px] hover:bg-[#FAFAFA] transition-colors"
                  >
                    <div className="flex-1">
                      <div 
                        className="font-semibold text-[16px] text-[#111111] hover:text-[#F97316] cursor-pointer transition-colors"
                        onClick={() => navigate(`/player/${u.username}`)}
                      >
                        {u.display_name || u.username}
                      </div>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-[12px] px-2 py-1 rounded-full border ${getTierColor(u.player_tier)}`}>
                          {u.player_tier || 'Unranked'}
                        </span>
                        <span className="text-[12px] text-[#71717A]">
                          {u.age_group || ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <PlayerFollowButton
                        userId={u.id}
                        username={u.username}
                        isFollowing={!!followingMap[userId]}
                        size="sm"
                        onFollowChange={(uid, newState) => {
                          setFollowingMap(prev => ({ ...prev, [String(uid)]: newState.following }));
                        }}
                      />
                      <Button
                        type="button"
                        variant={isBlocked ? "outline" : "destructive"}
                        size="sm"
                        disabled={!!blocking[userId]}
                        onClick={() => handleBlockClick(u)}
                        className={`rounded-[12px] ${isBlocked ? "border-orange-500 text-orange-600 hover:bg-orange-50" : ""}`}
                        data-testid={`block-btn-${u.username}`}
                      >
                        {blocking[userId] ? '...' : isBlocked ? 'Unblock' : 'Block'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* Block Confirmation Dialog */}
      <AlertDialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <AlertDialogContent className="rounded-[16px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[20px] font-bold text-[#111111]">Block {userToBlock?.username}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[14px] text-[#52525B]">
              This user will no longer be able to interact with your content. You can unblock them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToBlock(null)} className="rounded-[12px]">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeBlock}
              className="bg-red-600 hover:bg-red-700 rounded-[12px]"
            >
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Followers;
